import React, { useEffect, useState } from 'react';
import CameraCapture from '../CameraCapture';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import LoanExpensesEditor from './LoanExpensesEditor';
import { toCurrency } from '../../utils/adminMetrics';
import DocumentTile, { DOCUMENT_GRID } from '../UI/DocumentTile';
import CreditReportField, { useCreditReportGuide } from '../affordability/CreditReportField';
import DisplayField from '../UI/DisplayField';
import StatusBadge from '../UI/StatusBadge';

const LoanForm = ({
  loanForm,
  setLoanForm,
  loanFormErrors,
  interestRates,
  deductionDay,
  setDeductionDay,
  bankStatementFile,
  setBankStatementFile,
  payslipFile,
  setPayslipFile,
  // The client's credit report (ITC / TransUnion) and its date, collected
  // with the statement and payslip when no pre-qualification holds them.
  bureauFile = null,
  setBureauFile = null,
  bureauDate = '',
  setBureauDate = null,
  clientPhotoFile,
  setClientPhotoFile,
  canAddLoan,
  editMode = false,
  canEditField = () => true,
  showApplicationType = false,
  hideDocuments = false,
  // Chosen additional expense codes plus a setter. Omit either and the editor
  // is not rendered, so callers that predate it are unaffected.
  expenseCodes = null,
  setExpenseCodes = null,
  // Set when the repayment period was chosen on the pre-qualification result:
  // a client sees that period, fixed, rather than the default single month.
  durationChosenFromScore = false,
  // The completed credit assessment the loan is applied on: the quote then
  // checks the amount against what the client qualified for.
  creditAssessmentId = null,
  // Staff only: the client the loan is for.
  clientId = null,
  // A client applying on an assessment: what the document analysis filled in,
  // which they may not change (the API applies it). lockedDeductionDay: the
  // day from the salary date (null: not found, so they enter it).
  // maxPrincipal: the most they qualified for on the term (they may ask for less).
  lockedDeductionDay = null,
  maxPrincipal = null,
  // A client applying: the start and due dates follow the deduction day
  // (the API sets them); the client never edits them.
  lockDates = false,
}) => {
  // The credit report's label, help and messages (GET /api/credit-report-guide).
  const creditGuide = useCreditReportGuide();
  const [loanReasons, setLoanReasons] = useState([]);
  // What the loan costs, from the API (GET /api/loans/quote), and the
  // messages it gave for any field it could not accept.
  const [quote, setQuote] = useState(null);
  const [quoteErrors, setQuoteErrors] = useState({});

  // Fetch loan reasons once
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-reasons'), { headers: { Authorization: `Bearer ${token}` } });
        setLoanReasons(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setLoanReasons([]);
      }
    };
    fetchReasons();
  }, []);

  // Track if user manually edited dates so auto-calc won't override
  const [manualStartEdited, setManualStartEdited] = useState(false);
  const [manualDueEdited, setManualDueEdited] = useState(false);

  // The deduction dates come from the API (GET /api/loans/schedule), the one
  // place they are worked out, so the form, the mobile app and the stored loan
  // agree. A month-end salary is deducted on each month's last day.
  // With startDate (typed in by staff) the schedule runs from it and gives the
  // deduction day too.
  const fetchSchedule = async (dDay, durationStr, startDate = null) => {
    const d = parseInt(dDay, 10);
    const duration = parseInt(durationStr, 10);
    if (!duration || (!startDate && !d)) return { start: '', due: '', day: null };
    const res = await axios.get(apiUrl('/api/loans/schedule'), {
      params: startDate ? { start_date: startDate, duration_months: duration } : { deduction_day: d, duration_months: duration },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return { start: res.data?.start_date || '', due: res.data?.due_date || '', day: res.data?.deduction_day ?? null };
  };

  const handleResetDates = async () => {
    try {
      const { start, due } = await fetchSchedule(deductionDay, loanForm.duration_months);
      setManualStartEdited(false);
      setManualDueEdited(false);
      setLoanForm(form => ({ ...form, start_date: start, due_date: due }));
    } catch (_) {}
  };

  const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  // When the caller manages expenses, the chosen codes go to the quote;
  // otherwise the API applies its default expenses.
  const managesExpenses = Array.isArray(expenseCodes) && typeof setExpenseCodes === 'function';
  // Staff choose the rate; a client is charged the company rate, which the
  // API sets (and returns in the quote).
  const staffChoosesRate = showApplicationType;
  const expenseKey = managesExpenses ? JSON.stringify(expenseCodes) : null;

  // The amounts — principal + interest, the expenses, the total and the
  // monthly payment — come from the API, the one place they are worked out,
  // so the form shows exactly what the loan will store. They are copied onto
  // the form for the contract preview. A field the API cannot accept comes
  // back with the message to show under it.
  useEffect(() => {
    const principal = parseFloat(loanForm.principal_amount);
    const duration = parseInt(loanForm.duration_months, 10);
    const clearFigures = (form) => ({
      ...form,
      principal_amount_plus_interest: '',
      monthly_payment: '',
      interest: '',
      additional_expenses: undefined,
      additional_expenses_total: '',
      total_repayable: '',
      quote_errors: null,
    });
    if (!principal || !duration || (staffChoosesRate && !isFilled(loanForm.interest_rate))) {
      setQuote(null);
      setQuoteErrors({});
      setLoanForm(clearFigures);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = { principal_amount: loanForm.principal_amount, duration_months: duration };
        if (isFilled(deductionDay)) params.deduction_day = deductionDay;
        if (creditAssessmentId) params.credit_assessment_id = creditAssessmentId;
        if (managesExpenses) params.additional_expenses = expenseKey;
        if (staffChoosesRate) params.interest_rate = loanForm.interest_rate;
        if (staffChoosesRate && clientId) params.client_id = clientId;
        const res = await axios.get(apiUrl('/api/loans/quote'), {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (cancelled) return;
        const q = res.data;
        setQuote(q);
        setQuoteErrors({});
        setLoanForm(form => ({
          ...form,
          interest_rate: staffChoosesRate ? form.interest_rate : String(q.interest_rate),
          principal_amount_plus_interest: q.principal_amount_plus_interest,
          monthly_payment: q.monthly_payment,
          interest: q.interest,
          additional_expenses: q.expenses,
          additional_expenses_total: q.expenses_total,
          total_repayable: q.total_repayable,
          // The penalty clause a new agreement carries (shown in the contract preview).
          penalty_clause: q.penalty_clause || null,
          quote_errors: null,
        }));
      } catch (err) {
        if (cancelled) return;
        const data = err.response?.data || {};
        const byField = {};
        (Array.isArray(data.errors) ? data.errors : []).forEach((e) => {
          if (e?.field && !byField[e.field]) byField[e.field] = e.message;
        });
        if (!Object.keys(byField).length) byField.quote = data.error || data.message || 'The loan amounts could not be worked out. Please try again.';
        setQuote(null);
        setQuoteErrors(byField);
        setLoanForm(form => ({ ...clearFigures(form), quote_errors: byField }));
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [loanForm.principal_amount, staffChoosesRate ? loanForm.interest_rate : null, loanForm.duration_months, deductionDay, expenseKey, creditAssessmentId, clientId]);

  const fieldError = (field) => loanFormErrors[field] || quoteErrors[field];
  // The deduction day the documents set; the dates follow from it.
  const dayLocked = !!lockedDeductionDay;
  const datesLocked = dayLocked || lockDates;
  const ordinal = (n) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
  };
  const lockedDayText = dayLocked
    ? `${Number(lockedDeductionDay) === 31 ? 'The last day' : `The ${ordinal(Number(lockedDeductionDay))}`} of each month — from your salary date`
    : '';

  // Auto-fill the start and due dates from the deduction day and duration,
  // as the API works them out, unless they were typed in by hand.
  useEffect(() => {
    if (!deductionDay || !loanForm.duration_months) return undefined;
    let cancelled = false;
    fetchSchedule(deductionDay, loanForm.duration_months, manualStartEdited ? loanForm.start_date : null)
      .then(({ start, due }) => {
        if (cancelled || !start) return;
        setLoanForm(form => ({
          ...form,
          start_date: (!manualStartEdited || !form.start_date) ? start : form.start_date,
          due_date: (!manualDueEdited || !form.due_date) ? due : form.due_date,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [deductionDay, loanForm.duration_months]);

  // A start date typed in is the first deduction: the deduction day and the
  // final payment follow from it, as the schedule works them out.
  useEffect(() => {
    if (!manualStartEdited || !loanForm.start_date || !loanForm.duration_months) return undefined;
    let cancelled = false;
    fetchSchedule(null, loanForm.duration_months, loanForm.start_date)
      .then(({ due, day }) => {
        if (cancelled || !due) return;
        setManualDueEdited(false);
        setLoanForm((form) => ({ ...form, due_date: due }));
        if (day != null && !dayLocked && typeof setDeductionDay === 'function') setDeductionDay(String(day));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loanForm.start_date, manualStartEdited]);

  const mandatoryCls = (filled) => filled
    ? ''
    : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start [&>*]:!m-0">
      {/* Loan Details Section */}
      <div className="bg-blue-50 dark:bg-slate-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="4" />
              <path d="M6 20v-2a6 6 0 0112 0v2" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Loan Details</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Loan Reason</label>
            <select
              name="loan_reason_id"
              value={loanForm.loan_reason_id || ''}
              onChange={e => setLoanForm({ ...loanForm, loan_reason_id: e.target.value })}
              className={`w-full ${mandatoryCls(isFilled(loanForm.loan_reason_id))} disabled:opacity-60`}
              required
              disabled={!canAddLoan}
            >
              <option value="">Select reason</option>
              {loanReasons.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {loanFormErrors.loan_reason_id && <p className="text-rose-500 text-xs mt-1">{loanFormErrors.loan_reason_id}</p>}
          </div>
          {showApplicationType && !editMode && (
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Application Type</label>
              <select
                name="application_type"
                value={loanForm.application_type || ''}
                onChange={e => setLoanForm({ ...loanForm, application_type: e.target.value })}
                className={`w-full ${mandatoryCls(isFilled(loanForm.application_type))} disabled:opacity-60`}
                required
                disabled={!canAddLoan}
              >
                <option value="">Select type</option>
                <option value="Online">Online</option>
                <option value="Walk-in">Walk-in</option>
              </select>
              {loanFormErrors.application_type && <p className="text-rose-500 text-xs mt-1">{loanFormErrors.application_type}</p>}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Principal Amount</label>
            <input
              type="number"
              value={loanForm.principal_amount || ''}
              onChange={e => setLoanForm({ ...loanForm, principal_amount: e.target.value })}
              placeholder="Principal Amount"
              className={`w-full ${mandatoryCls(isFilled(loanForm.principal_amount))} disabled:opacity-60`}
              required
              // Cents allowed: the maximum offered is often not a whole number
              // (without a step the browser refuses N$11,381.25 on submit).
              step="0.01"
              min={maxPrincipal ? 1 : undefined}
              max={maxPrincipal || undefined}
              disabled={editMode ? !canEditField('principal_amount') : false}
            />
            {maxPrincipal > 0 && (
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Up to {toCurrency(maxPrincipal)} — you may borrow less</p>
            )}
            {fieldError('principal_amount') && <p className="text-rose-500 text-xs mt-1">{fieldError('principal_amount')}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">
              Interest Rate{!showApplicationType && (durationChosenFromScore ? ' (Fixed — the rate your score used)' : ' (Fixed)')}
            </label>
            {showApplicationType ? (
              <select
                value={loanForm.interest_rate || ''}
                onChange={e => setLoanForm({ ...loanForm, interest_rate: e.target.value })}
                className={`w-full ${mandatoryCls(isFilled(loanForm.interest_rate))} disabled:opacity-60`}
                required
                disabled={editMode ? !canEditField('interest_rate') : false}
              >
                <option value="">Select interest rate</option>
                {interestRates.map(rate => (
                  <option key={rate.id} value={rate.rate}>
                    {rate.rate}% - {rate.description}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={quote ? Number(quote.interest_rate).toFixed(2) : ''}
                placeholder="Set by the company"
                className="w-full disabled:opacity-60 cursor-not-allowed"
                required
                disabled={true}
                readOnly
              />
            )}
            {fieldError('interest_rate') && <p className="text-rose-500 text-xs mt-1">{fieldError('interest_rate')}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">
              Duration (months){!showApplicationType && (durationChosenFromScore ? ' (chosen with your score)' : ' (Fixed at 1 month)')}
            </label>
            {showApplicationType ? (
              <select
                value={loanForm.duration_months || ''}
                onChange={e => setLoanForm({ ...loanForm, duration_months: e.target.value })}
                className={`w-full ${mandatoryCls(isFilled(loanForm.duration_months))} disabled:opacity-60`}
                required
                disabled={editMode ? !canEditField('duration_months') : false}
              >
                <option value="">Select duration</option>
                <option value="1">1 Month</option>
                <option value="2">2 Months</option>
                <option value="3">3 Months</option>
                <option value="4">4 Months</option>
                <option value="5">5 Months</option>
                <option value="6">6 Months</option>
              </select>
            ) : (
              <input
                type="text"
                value={durationChosenFromScore ? (loanForm.duration_months || '1') : '1'}
                className="w-full disabled:opacity-60 cursor-not-allowed"
                required
                disabled={true}
                readOnly
              />
            )}
            {fieldError('duration_months') && <p className="text-rose-500 text-xs mt-1">{fieldError('duration_months')}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">First Deduction Day{dayLocked ? '' : ' (1-31)'}</label>
            {dayLocked ? (
              <input
                type="text"
                value={lockedDayText}
                className="w-full disabled:opacity-60 cursor-not-allowed"
                disabled={true}
                readOnly
              />
            ) : (
            <input
              type="number"
              value={deductionDay || ''}
              onChange={e => { setManualStartEdited(false); setDeductionDay(e.target.value); }}
              placeholder="Deduction Day"
              className={`w-full ${mandatoryCls(isFilled(deductionDay))} disabled:opacity-60`}
              required
              min={1}
              max={31}
              disabled={editMode ? !canEditField('deduction_day') : false}
            />
            )}
            {fieldError('deduction_day') && <p className="text-rose-500 text-xs mt-1">{fieldError('deduction_day')}</p>}
          </div>
        </div>
      </div>

      {/* Calculated Values Section */}
      <div className="bg-yellow-50 dark:bg-slate-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Calculated Values</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quoteErrors.quote && (
            <p className="md:col-span-2 text-rose-500 text-xs">{quoteErrors.quote}</p>
          )}
          {/* The figures worked out by the API's quote: the shared read-only fields. */}
          <DisplayField label="Principal + Interest">{quote ? toCurrency(quote.principal_amount_plus_interest) : null}</DisplayField>
          <DisplayField label="Additional Expenses">{quote ? toCurrency(quote.expenses_total) : null}</DisplayField>
          <div>
            <DisplayField label="Monthly Payment">{quote ? toCurrency(quote.monthly_payment) : null}</DisplayField>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Total payable (principal + interest + expenses) split evenly across the months.</p>
          </div>
          <div>
            <DisplayField label="Grand Total Loan Obligation" tone="strong">{quote ? toCurrency(quote.total_repayable) : null}</DisplayField>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{quote?.penalty_clause || 'Overdue penalty is charged on this total amount.'}</p>
          </div>
          {managesExpenses && (
            <div className="md:col-span-2">
              <LoanExpensesEditor
                value={expenseCodes}
                onChange={setExpenseCodes}
                priced={quote?.expenses ?? null}
                total={quote?.expenses_total ?? null}
                readOnly={editMode && !canEditField('principal_amount')}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Start Date</label>
            <div>
              <input
                type="date"
                value={loanForm.start_date || ''}
                onChange={e => {
                  const val = e.target.value;
                  setManualStartEdited(true);
                  setLoanForm(form => ({ ...form, start_date: val }));
                }}
                className="w-full disabled:opacity-60"
                disabled={datesLocked || (editMode ? !canEditField('start_date') : false)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Due Date</label>
            <div>
              <input
                type="date"
                value={loanForm.due_date || ''}
                onChange={e => {
                  const val = e.target.value;
                  setManualDueEdited(true);
                  setLoanForm(form => ({ ...form, due_date: val }));
                }}
                className="w-full disabled:opacity-60"
                disabled={datesLocked || (editMode ? !canEditField('due_date') : false)}
                min={loanForm.start_date || undefined}
              />
            </div>
          </div>
          {/* Reset Dates control (not needed when the dates follow a fixed day) */}
          {!datesLocked && (
          <div className="md:col-span-2">
            <button
              type="button"
              onClick={handleResetDates}
              className="mt-2 px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
              title="Restore auto-calculated Start and Due dates"
            >
              Reset Dates to Auto
            </button>
          </div>
          )}
          <DisplayField label="Status"><StatusBadge status="Pending" size="small" /></DisplayField>
        </div>
      </div>

      {/* Documents Section */}
      {!editMode && (!hideDocuments || setClientPhotoFile) && (
        <div className="xl:col-span-2 mt-10 bg-green-50 dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Required Documents</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {!hideDocuments && (
              <div className="col-span-1 md:col-span-2">
                <div className={DOCUMENT_GRID}>
                  <DocumentTile type="Bank Statement" hint="The last three months (PDF, max 10 MB)" file={bankStatementFile}
                    onPick={setBankStatementFile} onRemove={() => setBankStatementFile(null)} required disabled={!canAddLoan} />
                  <DocumentTile type="Payslip" label="Latest payslip" hint="PDF, max 10 MB" file={payslipFile}
                    onPick={setPayslipFile} onRemove={() => setPayslipFile(null)} required disabled={!canAddLoan} />
                  {setBureauFile && (
                    <CreditReportField
                      guide={creditGuide}
                      file={bureauFile}
                      onPick={setBureauFile}
                      onRemove={() => setBureauFile(null)}
                      reportDate={bureauDate}
                      onDate={setBureauDate}
                      required
                    />
                  )}
                </div>
                {loanFormErrors.bankStatementFile && <p className="text-rose-500 text-xs mt-1">{loanFormErrors.bankStatementFile}</p>}
                {loanFormErrors.payslipFile && <p className="text-rose-500 text-xs mt-1">{loanFormErrors.payslipFile}</p>}
                {loanFormErrors.bureauReport && <p className="text-rose-500 text-xs mt-1">{loanFormErrors.bureauReport}</p>}
              </div>
            )}
            {/* Only show Client Photo ID section if setClientPhotoFile is provided (client side application) */}
            {setClientPhotoFile && (
              <div className="col-span-1 md:col-span-2 mt-4">
                <label className="block text-center text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Client Photo ID</label>
                <div onClick={(e) => e.preventDefault()}>
                  <CameraCapture 
                    onPhotoCapture={(file, imageUrl) => {
                      setClientPhotoFile(file);
                      // You can store the imageUrl in state if needed for preview
                    }}
                    buttonText="Take ID Photo"
                  />
                </div>
                {loanFormErrors.clientPhotoFile && <p className="text-rose-500 text-xs mt-1">{loanFormErrors.clientPhotoFile}</p>}
                {clientPhotoFile && (
                  <div className="mt-2 p-2 bg-white dark:bg-slate-800 rounded border dark:border-slate-700">
                    <span className="text-sm text-gray-600 dark:text-slate-400">Photo captured successfully</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanForm;